/**
 * Creator Studio's dub controls, as one piece.
 *
 * The Voice/music positions, how much of the story timeline has no dubbed
 * voice, and the two runs this screen can start from that sum — the gap repair
 * and a first dub in a language the story has none in. The host binds what
 * comes back to the language toggle and mounts the popover; every decision
 * about which lines a run names, which mode it opens in, and what to re-read
 * afterwards is made here.
 *
 * Named for the SURFACE, like the composition control it wraps. Nothing here
 * asks whether the story is a reel, and nothing would change if it did: a reel
 * is this same timeline arranged into Sections cut from the one recording
 * (`InsertReelSectionsCommand` DS4), so it is measured exactly like any other
 * edit.
 */
import { useEditor, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useMemo, useState } from 'react';

import {
  type DubDurationLimitNote,
  getDubDurationLimitNote,
} from './dubDurationLimit';
import { useCreatorStudioDubComposition } from './useCreatorStudioDubComposition';
import { useDubCoverage } from './useDubCoverage';
import { useDubGenerateModalController } from './useDubGenerateModalController';
import { useKeepOriginalMusic } from './useKeepOriginalMusic';
import { useMusicSeparationController } from './useMusicSeparationController';

import type { DubGenerateModalProps } from './DubGenerateModal';
import type { CreatorStudioDubCompositionControl } from './useCreatorStudioDubComposition';
import type { MusicSeparationController } from './useMusicSeparationController';
import type { DubCoverage } from '@src/editor/dubbing/DubCoverageService';

export interface CreatorStudioDubGeneration {
  /** The Voice control's state and dispatchers, read off the story timeline. */
  composition: CreatorStudioDubCompositionControl;
  /** The story's separation controller, with the timeline's music position. */
  musicController: MusicSeparationController;
  /**
   * The timeline's gap sum in the language the CONTROL is showing — what the
   * notice describes. `null` while there is no answer yet.
   */
  coverage: DubCoverage | null;
  /** Dub just the lines the timeline is missing. */
  onFillGaps: () => void;
  /**
   * Re-record just the timeline's lines whose words were edited after they were
   * dubbed. The opposite run to `onFillGaps`, which is additive and therefore
   * leaves an already-voiced line saying its older words.
   */
  onReDubStale: () => void;
  /**
   * Dub the timeline in the language the transcript is showing, which has no
   * dub at all. Feeds `TranslationToggle`'s `onRequestFirstDub` and carries its
   * name. `undefined` on a workspace with dubbing switched off — the screen
   * then offers no way in to a dub run.
   */
  onRequestFirstDub: (() => void) | undefined;
  /**
   * PLAT-11877 — re-record a language's whole dub from the language menu's
   * row actions, as the clip preview offers. `undefined` with dubbing off.
   */
  onRequestRegenerateDub: ((language: string) => void) | undefined;
  /** A run is in flight, so the notice's action paints as working. */
  busy: boolean;
  /**
   * PLAT-11532 — why no dub can start here, or `undefined` when one
   * can. Both entry points refuse while it is set; the host hands it to the
   * toggle and the coverage notice, which disable their actions and say why.
   */
  dubUnavailableNote: DubDurationLimitNote | undefined;
  /**
   * Everything `DubGenerateModal` needs bar the workspace's language list, or
   * `null` when the popover is closed (or the story has no id to dub).
   */
  modalProps: Omit<DubGenerateModalProps, 'supportedLanguages'> | null;
}

export function useCreatorStudioDubGeneration({
  selectedLanguage,
  onReviewDub,
}: {
  /** The transcript's selected language. `null` = Original. */
  selectedLanguage: string | null;
  /**
   * PLAT-11877 — opens Dubbing Review for a language whose run just landed;
   * the popover's "ready" toast offers it as "Review Dub", as the clip
   * preview's does. Absent, the toast has no action.
   */
  onReviewDub?: (language: string) => void;
}): CreatorStudioDubGeneration {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();
  const userIdentity = useUserIdentity();
  const { dubbingMaxMinutes, stemSeparationMaxMinutes } = useFlagsCombination(
    userIdentity.currentRole,
  );

  /**
   * PLAT-11532 — why no dub can start here, measured against the TIMELINE's own
   * length rather than the recording's.
   *
   * The runs this screen starts cover the timeline's lines, and Clip Preview
   * gates on the clip's length the same way, so the length that decides is the
   * one being published. Gating on the whole recording would refuse a
   * six-minute edit cut from a twelve-minute recording — and turning sections
   * off is what leaves the gap this screen exists to show, so that is the case
   * it would refuse most.
   *
   * `clip` scope, not `story`: what it measured is a portion, so the line
   * states the length alone. Calling it the story would print a number the
   * reader can check against the story and find wrong. A timeline with no
   * length yet is not gated — a working dub beats a guess.
   */
  const timelineDurationSec =
    editorStore.editor?.timeline.duration || undefined;
  const dubUnavailableNote = getDubDurationLimitNote(
    timelineDurationSec,
    dubbingMaxMinutes,
    'clip',
  );

  // The only separation started from here is the dub popover's "Keep Original
  // Music" box, which runs after the popover has closed — so the outcome is a
  // toast, as on the other two surfaces.
  const musicSeparation = useMusicSeparationController(
    videoCreator.storyId ?? '',
    {
      onDone: () => {
        videoCreator.showToast({
          state: 'success',
          message: 'Voice and music separated',
        });
      },
      onError: () => {
        videoCreator.showToast({
          state: 'error',
          message: "Couldn't separate voice and music. Try again.",
        });
      },
    },
  );

  const dubComposition = useCreatorStudioDubComposition({
    selectedLanguage,
    musicController: musicSeparation,
  });

  /**
   * PLAT-11555 — how much of the REEL has no dubbed voice.
   *
   * The same sum the clip preview shows, pointed at the story editor instead of
   * a clip-scoped one, so it measures the stretches the timeline actually
   * keeps. It is the timeline and not the whole recording because a section put
   * back after the
   * dub was made is exactly the case this is for: the recording's other lines
   * are not published and their missing audio is not a defect.
   *
   * Keyed on the language the CONTROL is showing, and — for the button, which
   * offers a dub in a language the story has none in — on the one being READ.
   * They are the same language most of the time and the hook drops the repeat.
   */
  const [dubCoverageRefresh, setDubCoverageRefresh] = useState(0);
  const isStoryOriginalVideoElement = useCallback(
    (probe: unknown) => videoCreator.isOriginalVideoElement(probe),
    [videoCreator],
  );
  const dubCoverageByLanguage = useDubCoverage({
    editorStore,
    isOriginalVideoElement: isStoryOriginalVideoElement,
    languages: [dubComposition.controlLanguage, selectedLanguage],
    refreshToken: dubCoverageRefresh,
  });
  const dubCoverage = dubComposition.controlLanguage
    ? (dubCoverageByLanguage[dubComposition.controlLanguage] ?? null)
    : null;
  const selectedLanguageCoverage = selectedLanguage
    ? (dubCoverageByLanguage[selectedLanguage] ?? null)
    : null;

  /**
   * Dub just the lines the timeline is missing.
   *
   * This screen has no other way in to a dub run — generating one from scratch
   * stays on the Transcript & Speakers tab — and deliberately so: what is
   * offered here is a repair for a gap this screen is the one that shows, in the
   * language it is showing it in, and nothing wider. The run is additive, so
   * pressing it never re-records a line already voiced and approved.
   */
  const fillGapsModal = useDubGenerateModalController();
  const gapPhraseIds = dubCoverage?.gaps.map(gap => gap.phraseId) ?? [];
  const handleFillGaps = useCallback(() => {
    const language = dubComposition.controlLanguage;
    if (!language || gapPhraseIds.length === 0 || dubUnavailableNote) return;
    fillGapsModal.open(language, {
      segmentIds: gapPhraseIds,
      fillGaps: true,
    });
  }, [
    dubComposition.controlLanguage,
    dubUnavailableNote,
    fillGapsModal,
    gapPhraseIds,
  ]);

  /**
   * Re-record the timeline's lines whose words were edited after they were
   * dubbed.
   *
   * The popover's third scoped mode — `segmentIds` with neither `fillGaps` nor
   * `firstDub` — which FORCES re-synthesis of lines that already have audio
   * (`isSpanReDub` in `DubGenerateModal`) and reuses each speaker's current
   * voice. The gap fill above cannot do this by design: it is additive, so it
   * skips an out-of-date line for the same reason it never re-records an
   * approved one. Gated on the same cap: a timeline too long to dub is too long
   * to re-dub.
   */
  const stalePhraseIds = dubCoverage?.stale.map(line => line.phraseId) ?? [];
  const handleReDubStale = useCallback(() => {
    const language = dubComposition.controlLanguage;
    if (!language || stalePhraseIds.length === 0 || dubUnavailableNote) return;
    fillGapsModal.open(language, { segmentIds: stalePhraseIds });
  }, [
    dubComposition.controlLanguage,
    dubUnavailableNote,
    fillGapsModal,
    stalePhraseIds,
  ]);

  /**
   * Dub the timeline in a language the story has no dub in at all.
   *
   * With no dub anywhere, every one of the timeline's lines is a gap, so the gap
   * list already IS the list of lines to send. A press before the sum has come
   * back names no lines, and a run naming no lines would dub nothing, so it does
   * not start.
   */
  const selectedGapPhraseIds =
    selectedLanguageCoverage?.gaps.map(gap => gap.phraseId) ?? [];
  const handleFirstDub = useCallback(() => {
    if (
      !selectedLanguage ||
      selectedGapPhraseIds.length === 0 ||
      dubUnavailableNote
    )
      return;
    fillGapsModal.open(selectedLanguage, {
      segmentIds: selectedGapPhraseIds,
      firstDub: true,
    });
  }, [
    dubUnavailableNote,
    fillGapsModal,
    selectedGapPhraseIds,
    selectedLanguage,
  ]);

  /** PLAT-11877 — the row action's whole-language re-dub (replace mode). */
  const handleRegenerateDub = useCallback(
    (language: string) => {
      if (dubUnavailableNote) return;
      fillGapsModal.open(language, { replace: true });
    },
    [dubUnavailableNote, fillGapsModal],
  );

  // The story's separation controller with its two music fields replaced: on
  // this screen the music position must be read off the timeline's elements
  // rather than from the controller's own throwaway browser state.
  const storyMusicController = useMemo(
    () => ({
      ...musicSeparation,
      musicMode: dubComposition.musicMode,
      setMusicMode: dubComposition.onMusicModeChange ?? (() => undefined),
    }),
    [
      dubComposition.musicMode,
      dubComposition.onMusicModeChange,
      musicSeparation,
    ],
  );

  const storyDurationSec = videoCreator.story?.originalVideo?.video?.duration;
  // The popover's "Keep Original Music" box. On the timeline-backed controller,
  // so the music lands on the story timeline once the bed exists.
  const keepOriginalMusic = useKeepOriginalMusic({
    controller: storyMusicController,
    durationSec: storyDurationSec,
    maxMinutes: stemSeparationMaxMinutes,
  });

  const handleDubSuccess = useCallback(() => {
    // The manifest changed on the server and nothing here observes that, so
    // re-read it — otherwise the warning goes on describing the gap it just
    // filled.
    setDubCoverageRefresh(token => token + 1);
    if (fillGapsModal.isFirstDub) {
      // A FIRST dub creates a dub this story did not have, and two things on
      // this screen were written for runs that repair one.
      //
      // The dubbed-language list is read once when the surface opens, so
      // without a re-read the Voice toggle never appears and the button goes
      // on offering a dub that now exists.
      //
      // And the timeline is still on Original, which is exactly the state that
      // makes `reapplyComposition` do nothing. Putting it on the dub it just
      // made is what makes the new voice audible without a reload.
      dubComposition.refreshDubbedLanguages?.();
      dubComposition.onAudioModeChange?.('dubbed');
      return;
    }
    // Otherwise fetch the description again. Re-reading the warning alone said
    // the gap was filled while the timeline went on playing silence over it,
    // because the audio in hand was still built from the old manifest. Neither
    // control position moves.
    dubComposition.reapplyComposition?.();
  }, [
    dubComposition.onAudioModeChange,
    dubComposition.reapplyComposition,
    dubComposition.refreshDubbedLanguages,
    fillGapsModal.isFirstDub,
  ]);

  const storyId = videoCreator.storyId;
  // `storyDurationSec` (above) is the recording's own length, which is the
  // engine's ceiling rather than the cap above: the two measure different things.
  const modalProps =
    fillGapsModal.isOpen && fillGapsModal.targetLanguage && storyId
      ? {
          storyId,
          targetLanguage: fillGapsModal.targetLanguage,
          segmentIds: fillGapsModal.segmentIds ?? undefined,
          fillGaps: fillGapsModal.isFillGaps,
          firstDub: fillGapsModal.isFirstDub,
          /* Hand the engine the room the dub really has: the source video's
             whole length. Its own default ceiling is the last spoken word,
             which a translation always overruns, so without this every gap
             fill re-records all of the story's lines. */
          availableDurationMs: storyDurationSec
            ? Math.round(storyDurationSec * 1000)
            : undefined,
          music: keepOriginalMusic,
          onClose: fillGapsModal.close,
          onSuccess: handleDubSuccess,
          successAction: onReviewDub
            ? (language: string) => ({
                label: 'Review Dub',
                onClick: () => onReviewDub(language),
              })
            : undefined,
        }
      : null;

  return {
    composition: dubComposition,
    musicController: storyMusicController,
    coverage: dubCoverage,
    onFillGaps: handleFillGaps,
    onReDubStale: handleReDubStale,
    onRequestFirstDub: dubComposition.onAudioModeChange
      ? handleFirstDub
      : undefined,
    onRequestRegenerateDub: dubComposition.onAudioModeChange
      ? handleRegenerateDub
      : undefined,
    busy: fillGapsModal.isOpen,
    dubUnavailableNote,
    modalProps,
  };
}
